import { Link } from "react-router-dom";
import {
  ShieldCheck,
  Package,
  Sparkles,
  Calendar,
  Layers,
  Boxes,
  Tag,
  Scale,
  ListChecks,
  Home,
  Building2,
  Palette,
  ArrowRight,
} from "lucide-react";
import BigDemenageurCTA from "@/components/BigDemenageurCTA";

const Sections = ({ city }) => (
  <>
    {/* ======= SECTION 2 — Livraison à domicile à Orgeval ======= */}
    <section className="py-14 md:py-20 bg-[#EEF3F7] border-y border-[#cfdbe6]">
      <div className="container-cdm">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          <div className="lg:col-span-7">
            <div className="overline" style={{ color: "#237cb5" }}>
              Livraison à domicile
            </div>
            <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
              Livraison de vos cartons de déménagement à domicile
              {" "}{city.aCity}
            </h2>
            <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
              Parce que la logistique d&apos;un déménagement demande de la
              rigueur, notre service de livraison {city.aCity} et ses
              environs (Morainvilliers, Villennes-sur-Seine,
              Feucherolles, Poissy) repose sur deux piliers absolus.
            </p>
          </div>

          <div className="lg:col-span-5 space-y-4">
            {[
              {
                title: "Ponctualité millimétrée",
                body: "Nous convenons ensemble d'un créneau horaire précis selon votre planning. Pas de tranche horaire floue ni d'attente interminable : votre livreur arrive à l'heure convenue pour que vous puissiez débuter vos préparatifs sans perdre une minute.",
                Icon: Calendar,
              },
              {
                title: "Qualité et propreté",
                body: "L'intégralité de vos fournitures vous est remise à plat sous film de protection hermétique. Nous vous garantissons des cartons parfaitement secs et propres, préservant 100% de la rigidité de leur double cannelure d'origine pour une sécurité maximale.",
                Icon: ShieldCheck,
              },
            ].map(({ title, body, Icon }) => (
              <div
                key={title}
                className="flex items-start gap-4 bg-white border border-[#cfdbe6] rounded-2xl p-5 md:p-6"
              >
                <span className="flex items-center justify-center shrink-0 h-11 w-11 rounded-full bg-[#237cb5] text-white">
                  <Icon className="h-5 w-5" strokeWidth={2.4} />
                </span>
                <div>
                  <h3 className="font-display font-extrabold text-[15.5px] text-[#0f1d2b] leading-tight">
                    {title}
                  </h3>
                  <p className="mt-2 text-[14px] text-[#3a4856] leading-relaxed">
                    {body}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>

    {/* ======= SECTION 3 — Conseils d'organisation ======= */}
    <section className="py-14 md:py-20 bg-white">
      <div className="container-cdm">
        <div className="max-w-3xl">
          <div className="overline" style={{ color: "#237cb5" }}>
            Méthode pro
          </div>
          <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
            Les clés d&apos;un emballage méthodique
          </h2>
          <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
            Pour gagner un temps précieux et éviter le chaos lors du
            déballage, voici les règles d&apos;or à suivre.
          </p>
        </div>

        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
          {[
            {
              title: "Codage par pièce et par couleur",
              body: "Utilisez un marqueur de couleur différente pour chaque pièce de votre future habitation à Orgeval (ex : Rouge pour la cuisine, Bleu pour la chambre parentale) et notez-le sur les deux côtés du carton.",
              Icon: Palette,
            },
            {
              title: "Répartition intelligente du poids",
              body: "Ne remplissez jamais un grand carton uniquement avec des livres ou des objets lourds. Réservez les petits cartons renforcés pour le lourd (livres, dossiers) et les grands volumes pour le léger (linge, couettes).",
              Icon: Scale,
            },
            {
              title: "Inventaire du contenu",
              body: "Inscrivez brièvement sur chaque boîte les objets principaux qu'elle contient. Cela vous évitera d'ouvrir dix cartons différents le premier soir pour retrouver vos clés, vos chargeurs ou la cafetière.",
              Icon: ListChecks,
            },
          ].map(({ title, body, Icon }) => (
            <div
              key={title}
              className="flex items-start gap-4 bg-[#EEF3F7] border border-[#cfdbe6] rounded-2xl p-5 md:p-6"
            >
              <span className="flex items-center justify-center shrink-0 h-11 w-11 rounded-full bg-[#237cb5] text-white">
                <Icon className="h-5 w-5" strokeWidth={2.4} />
              </span>
              <div>
                <h3 className="font-display font-extrabold text-[15.5px] text-[#0f1d2b] leading-tight">
                  {title}
                </h3>
                <p className="mt-2 text-[14px] text-[#3a4856] leading-relaxed">
                  {body}
                </p>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-8 flex flex-wrap gap-3">
          <Link
            to="/fermeture-accessoires"
            className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-[#237cb5] hover:bg-[#1f6da0] text-white font-bold text-[14px] transition-colors"
            data-testid="city-orgeval-cta-accessoires"
          >
            <Tag className="h-4 w-4" strokeWidth={2.6} />
            Marqueurs &amp; adhésifs pro
            <ArrowRight className="h-4 w-4" strokeWidth={2.6} />
          </Link>
          <Link
            to="/kits-cartons"
            className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-white border border-[#cfdbe6] hover:border-[#237cb5] hover:text-[#237cb5] text-[#0f1d2b] font-bold text-[14px] transition-colors"
            data-testid="city-orgeval-cta-kits"
          >
            <Boxes className="h-4 w-4" strokeWidth={2.6} />
            Voir nos kits complets
          </Link>
        </div>
      </div>
    </section>

    {/* ======= CTA Big Déménagement (au milieu) ======= */}
    <section className="py-12 bg-[#EEF3F7] border-y border-[#cfdbe6]">
      <div className="container-cdm">
        <BigDemenageurCTA testId={`city-${city.slug}-bigdem-cta-middle`} />
      </div>
    </section>

    {/* ======= SECTION 4 — Équipements petits & grands volumes ======= */}
    <section className="py-14 md:py-20 bg-white">
      <div className="container-cdm">
        <div className="max-w-3xl">
          <div className="overline" style={{ color: "#237cb5" }}>
            Petits &amp; grands volumes
          </div>
          <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
            Des équipements adaptés aux petits comme aux grands volumes
          </h2>
          <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
            Chaque déménagement {city.aCity} possède sa propre identité
            et ses propres contraintes de volume. C&apos;est pourquoi
            nous refusons le principe du matériel unique et développons
            des solutions calibrées pour chaque typologie de projet.
          </p>
        </div>

        <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-5">
          {/* Petits volumes */}
          <div className="bg-[#EEF3F7] border border-[#cfdbe6] rounded-2xl p-6 md:p-7">
            <div className="flex items-center gap-3">
              <span className="flex items-center justify-center h-11 w-11 rounded-full bg-[#237cb5] text-white">
                <Home className="h-5 w-5" strokeWidth={2.4} />
              </span>
              <h3 className="font-display font-extrabold text-[18px] text-[#0f1d2b] leading-tight">
                Pour les petits volumes (Studios, T1, T2 &amp; bureaux)
              </h3>
            </div>
            <p className="mt-4 text-[14.5px] text-[#3a4856] leading-relaxed">
              La priorité est donnée à l&apos;agilité et à
              l&apos;optimisation de l&apos;espace. Nous mettons à votre
              disposition des cartons standards polyvalents et des
              caisses compactes, faciles à porter et à empiler dans un
              petit véhicule utilitaire.
            </p>
            <p className="mt-3 text-[14.5px] text-[#3a4856] leading-relaxed">
              Pour ces espaces souvent habités par des profils dynamiques
              ou étudiants, nos kits intègrent l&apos;essentiel :
              adhésifs de scellage à forte adhérence et film à bulles
              d&apos;air pour sécuriser le matériel informatique, les
              écrans et la petite vaisselle, évitant ainsi l&apos;achat
              de surplus inutile.
            </p>
            <div className="mt-5 space-y-2 text-[13px] text-[#3a4856]">
              <div className="flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-[#237cb5]" strokeWidth={2.6} />
                Cartons standards polyvalents
              </div>
              <div className="flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-[#237cb5]" strokeWidth={2.6} />
                Caisses compactes faciles à porter
              </div>
              <div className="flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-[#237cb5]" strokeWidth={2.6} />
                Adhésifs forte adhérence
              </div>
              <div className="flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-[#237cb5]" strokeWidth={2.6} />
                Film à bulles pour high-tech
              </div>
            </div>
          </div>

          {/* Grands volumes */}
          <div className="bg-[#EEF3F7] border border-[#cfdbe6] rounded-2xl p-6 md:p-7">
            <div className="flex items-center gap-3">
              <span className="flex items-center justify-center h-11 w-11 rounded-full bg-[#237cb5] text-white">
                <Building2 className="h-5 w-5" strokeWidth={2.4} />
              </span>
              <h3 className="font-display font-extrabold text-[18px] text-[#0f1d2b] leading-tight">
                Pour les grands volumes (Maisons, propriétés &amp;
                pavillons)
              </h3>
            </div>
            <p className="mt-4 text-[14.5px] text-[#3a4856] leading-relaxed">
              Les habitations de grande superficie exigent des structures
              capables de supporter des charges lourdes et des
              manipulations répétées. Notre gamme se déploie alors en
              version haute performance.
            </p>
            <p className="mt-3 text-[14.5px] text-[#3a4856] leading-relaxed">
              Cartons à double cannelure renforcée pour résister à
              l&apos;écrasement vertical lorsque les boîtes sont empilées
              sur plusieurs mètres dans le camion, cartons penderies
              verticaux à tringles métalliques pour déplacer vos
              dressings sur cintres sans froisser vos vêtements, et
              cartons à croisillons spécifiques pour isoler
              individuellement vos verres et assiettes. À cela
              s&apos;ajoutent nos équipements de manutention lourde
              (diables et chariots à roulettes) indispensables pour
              déplacer des piles de quatre cartons ou de
              l&apos;électroménager sur de longues distances sans aucun
              effort physique.
            </p>
            <div className="mt-5 space-y-2 text-[13px] text-[#3a4856]">
              <div className="flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-[#237cb5]" strokeWidth={2.6} />
                Double cannelure haute performance
              </div>
              <div className="flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-[#237cb5]" strokeWidth={2.6} />
                Cartons penderies à tringles
              </div>
              <div className="flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-[#237cb5]" strokeWidth={2.6} />
                Croisillons verres &amp; assiettes
              </div>
              <div className="flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-[#237cb5]" strokeWidth={2.6} />
                Diables &amp; chariots à roulettes
              </div>
            </div>
          </div>
        </div>

        <div className="mt-8 flex flex-wrap gap-3">
          <Link
            to="/cartons-demenagement"
            className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-[#237cb5] hover:bg-[#1f6da0] text-white font-bold text-[14px] transition-colors"
            data-testid="city-orgeval-cta-shop"
          >
            <Boxes className="h-4 w-4" strokeWidth={2.6} />
            Voir toute la gamme
            <ArrowRight className="h-4 w-4" strokeWidth={2.6} />
          </Link>
          <Link
            to="/fermeture-accessoires"
            className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-white border border-[#cfdbe6] hover:border-[#237cb5] hover:text-[#237cb5] text-[#0f1d2b] font-bold text-[14px] transition-colors"
            data-testid="city-orgeval-cta-manutention"
          >
            <Layers className="h-4 w-4" strokeWidth={2.6} />
            Diables &amp; manutention
          </Link>
        </div>
      </div>
    </section>

    {/* ======= SECTION 5 — CTA retour département ======= */}
    <section className="py-10 md:py-14 bg-[#EEF3F7] border-t border-[#cfdbe6]">
      <div className="container-cdm">
        <Link
          to={`/cartons-demenagement/${city.deptSlug}`}
          className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-white border border-[#cfdbe6] hover:border-[#237cb5] hover:text-[#237cb5] text-[#0f1d2b] font-bold text-[14px] transition-colors"
          data-testid="city-orgeval-cta-back-dept"
        >
          <Package className="h-4 w-4" strokeWidth={2.6} />
          Voir la page {city.deptName} ({city.deptCode})
        </Link>
      </div>
    </section>
  </>
);

export default { Sections };
